import * as dm from '../domain';
import DomainModel from '../domain-model';
import getLogger from '../logging';
import { DefaultsConfig } from './defaults-config-options';
import { match, firstof, Action, anyof, always, prop, isInstanceOf } from '../control-flow/action-on-match';

const log = getLogger('defaults-manager');

/**
 * An alias for Material.fromJSON(...)
 */
const M = (data: unknown) => dm.Material.fromJSON(data);

export const reset = (c: DefaultsConfig): Action<DomainModel> => {
    return firstof(

        match(isInstanceOf(dm.Cubby), cubby => {
            cubby.faceMaterial = M(c.materials.edgeband);
            cubby.boxMaterial = M(c.materials.shelving);
        }),

        match(isInstanceOf(dm.Door), resetDoor(c)),
        match(isInstanceOf(dm.Drawer), resetDrawer(c)),
        match(isInstanceOf(dm.DrawerBox), resetDrawerBox(c)),
        match(isInstanceOf(dm.GenericAsset), resetGenericAsset(c)),
        match(isInstanceOf(dm.Handle), resetHandle(c)),
        match(isInstanceOf(dm.SlantedShelf), resetSlantedShelf(c)),
    );
}

export const resetDoor = (c: DefaultsConfig): Action<dm.Door> => {
    return anyof(
        always(door => {
            door.panel.style = c.styles.doorPanel;
            door.panel.material = M(c.materials.door);
        }),
        // TODO: this may properly belong in the add-door-tool
        match(d => d.handles.length == 0, door => {
            door.handles = [dm.Handle.fromJSON({}, door)];
        }),
        match(d => d.hinge == "double" && d.handles.length != 2, door => {
            const handle = dm.Handle.fromJSON(door.handles[0].toJSON(), door);
            door.handles = [handle, handle.clone(true)]
        }),
    );
}

export const resetDrawer = (c: DefaultsConfig): Action<dm.Drawer> => {
    return anyof(
        match(_ => c.styles.drawerFrame == null, drawer => {
            drawer.frame = null;
        }),
        match(d => d.frame != null, drawer => {
            drawer.frame.style = c.styles.drawerFrame;
            drawer.frame.material = M(c.materials.drawer);
        }),
        match(d => d.panel != null, drawer => {
            drawer.panel.grain = "horizontal";
            drawer.panel.style = c.styles.drawerPanel;
            drawer.panel.material = M(c.materials.drawer);
        }),
        match(d => d.box == null, drawer => {
            drawer.box = dm.DrawerBox.fromJSON({}, drawer);
        }),
    );
}

/**
 * A predicate to match a modelAssetId property on a DrawerBox.
 */
const modelAssetId = (value: RegExp) => prop<dm.DrawerBox>('modelAssetId', value);

export const resetDrawerBox = (c: DefaultsConfig): Action<dm.DrawerBox> => {

    return firstof(

        match(b => b.modelAssetId == null, box => {
            box.materials = [M(c.materials.drawerBox)];
        }),

        match(modelAssetId(/^parts\/tag-curate/), resetTagCurate(c)),
        match(modelAssetId(/^parts\/tag-engage/), resetTagEngage(c)),
    );
}

export const resetTagCurate = (c: DefaultsConfig): Action<dm.DrawerBox> => {
    return firstof(

        match(modelAssetId(/jewelry|shelf-drawer|tie|sunglasses/), box => {
            box.materials =
                [M(c.materials.tagFrame), M(c.materials.tagLeatherInsert)];
        }),

        match(modelAssetId(/pants/), box => {
            box.materials =
                [M(c.materials.tagFrame), M(c.materials.tagBlack)];
        }),

        match(modelAssetId(/(small|medium|large)-basket/), box => {
            box.materials =
                [M(c.materials.tagFrame), M(c.materials.tagCurateFabric)];
        }),

        match(modelAssetId(/laundry/), box => {
            box.materials = [
                M(c.materials.tagFrame),
                M(c.materials.tagCurateFabric),
                M(c.materials.tagBlack)];
        }),

        match(modelAssetId(/shoe/), box => {
            box.materials = [
                M(c.materials.tagFrame),
                M(c.materials.tagMeshInsert),
                M(c.materials.tagBlack)];
        }),

    );
}

export const resetTagEngage = (c: DefaultsConfig): Action<dm.DrawerBox> => {
    return firstof(

        match(modelAssetId(/laundry/), box => {
            box.materials = [
                M(c.materials.tagBlack),
                M(c.materials.tagFrame),
                M(c.materials.tagCanvas)];
        }),

        match(modelAssetId(/(pant)|(shoe)/), box => {
            box.materials = [M(c.materials.tagFrame), M(c.materials.tagBlack)];
        }),

        // the default case, only reached if the above predicates have failed
        always(box => {
            box.materials = [M(c.materials.tagFrame), M(c.materials.tagCanvas)];
        }),

    );
}

const assetId = <T>(value: RegExp) => prop<T>('assetId', value);
const filename = (value: RegExp) => prop<dm.GenericAsset>('filename', value);


export const resetGenericAsset = (c: DefaultsConfig): Action<dm.GenericAsset> => {
    return firstof(
        match(assetId(/illuminated-shelf/), asset => {
            asset.materials = [
                M(c.materials.tagFrame),
                M({assetId: "materials/glass", slot: "glass"}),
                M({type: "LightNeutral", slot: "light"})];
        }),
        match(filename(/RodPD/), asset => {
                asset.materials = [
                    M({ "type": "MatteBlack" }),
                    M(c.materials.hardware)];
        }),
    );
}

export const resetHandle = (c: DefaultsConfig): Action<dm.Handle> => {
    return handle => {
        handle.assetId = c.styles.hardware;
        handle.materials = [M(c.materials.hardware)];
    };
}

const fenceAssetId =
    (value: RegExp) => prop<dm.SlantedShelf>('fenceAssetId', value);

export const resetSlantedShelf = (c: DefaultsConfig): Action<dm.SlantedShelf> => {
    return anyof(
    match(s => s.fenceAssetId != null,
          firstof(
              match(fenceAssetId(/generic-01/), shelf => {
              }),
              match(fenceAssetId(/tag-elite/), shelf => {
              }),
              match(fenceAssetId(/tag-curate-leather-insert/), shelf => {
              }),
              match(fenceAssetId(/tag-curate-leather-wrapped/), shelf => {
              }),
              match(fenceAssetId(/tag-curate-plain/), shelf => {
              }),
    )),
    always(shelf => {
        shelf.shelfMaterials = [M(c.materials.shelving), M(c.materials.edgeband)];
    })
    );
}
